import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Copy, CheckCircle2, Shield } from "lucide-react";

/**
 * KiviHealth Bridge — while our own ABDM M1/M2/M3 approval is pending, we
 * lean on KiviHealth (already ABDM-approved) to actually mint an ABHA and
 * then capture the returned ABHA number/address back on the patient record.
 *
 * Flow:
 *  1. Reception clicks "Create ABHA on KiviHealth" on a patient/OPD/IPD/Walk-in row
 *  2. This dialog opens.  It shows the patient details already prepared in a
 *     clipboard-friendly block AND a big [Open KiviHealth →] button that
 *     both COPIES those details AND opens https://kivihealth.com/doctor/login
 *     in a new tab.
 *  3. Reception pastes the block into KiviHealth's new-patient form, walks
 *     through KiviHealth's ABDM flow, receives the ABHA number/address.
 *  4. They return to this dialog, enter the ABHA number + address, click
 *     "Save & Link" → we PUT /api/patients/{pid} to persist and audit.
 */
export default function KiviHealthBridge({ open, onOpenChange, patient, onLinked }) {
  const [abhaNumber, setAbhaNumber] = useState(patient?.abha_number || "");
  const [abhaAddress, setAbhaAddress] = useState(patient?.abha_address || "");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!patient) return null;

  // Formatted block optimised for a quick paste into KiviHealth's form.
  const block =
`Name: ${patient.name || ""}
DOB / Age: ${patient.age || ""} yrs
Gender: ${patient.gender || ""}
Mobile: ${patient.mobile || ""}
Address: ${patient.address || ""}
City: ${patient.city || ""}
State: ${patient.state || "Gujarat"}
Pincode: ${patient.pincode || ""}
Blood Group: ${patient.blood_group || ""}
UHID (Vyomra): ${patient.uhid || ""}`;

  const openKivi = async () => {
    try {
      await navigator.clipboard.writeText(block);
      setCopied(true);
      toast.success("Patient details copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — please copy the block below manually");
    }
    window.open("https://kivihealth.com/doctor/login", "_blank", "noopener");
  };

  const copyBlock = async () => {
    try {
      await navigator.clipboard.writeText(block);
      setCopied(true);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  const saveAbha = async () => {
    const num = abhaNumber.trim();
    if (num && !/^\d{2}-\d{4}-\d{4}-\d{4}$/.test(num)) {
      return toast.error("ABHA number should look like 91-1234-5678-9012");
    }
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, {
        ...patient,
        abha_number: abhaNumber.trim(),
        abha_address: abhaAddress.trim(),
      });
      toast.success("ABHA linked to patient");
      onLinked?.({ abha_number: abhaNumber.trim(), abha_address: abhaAddress.trim() });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ABHA");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="kivi-bridge">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-teal-700"/> Create ABHA via KiviHealth
            <Badge className="bg-teal-100 text-teal-800 rounded-sm">ABDM M1/M2/M3 approved</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="border bg-blue-50 border-blue-200 p-3 rounded-sm text-xs text-blue-900">
            While Vyomra HOS awaits its own ABDM approval, use KiviHealth (already M3-certified) to mint the ABHA.
            The patient details below are pre-formatted — one click copies them and opens KiviHealth's doctor login in a new tab.
          </div>

          {/* Patient block */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="label-tiny">Patient details (paste into KiviHealth's new-patient form)</Label>
              <button type="button" onClick={copyBlock} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="kivi-copy">
                {copied ? <CheckCircle2 className="w-3 h-3 text-teal-600"/> : <Copy className="w-3 h-3"/>}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="text-[11px] bg-slate-50 border p-3 rounded-sm font-mono whitespace-pre-wrap" data-testid="kivi-block">{block}</pre>
          </div>

          <Button onClick={openKivi} className="w-full bg-teal-700 hover:bg-teal-800 rounded-sm" data-testid="kivi-open">
            <ExternalLink className="w-4 h-4 mr-1"/> Copy details & Open KiviHealth Doctor Login
          </Button>

          <div className="border-t pt-3">
            <div className="label-tiny mb-2">Step 2 · Paste the ABHA back here</div>
            <p className="text-[11px] text-slate-500 mb-2">
              After KiviHealth creates the ABHA, copy the ABHA number and address from their screen and paste below to link them to <b>{patient.name}</b> (UHID {patient.uhid}).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">ABHA Number</Label>
                <Input
                  value={abhaNumber}
                  onChange={(e) => setAbhaNumber(e.target.value)}
                  placeholder="91-1234-5678-9012"
                  className="mt-1 font-mono"
                  data-testid="kivi-abha-number"
                />
              </div>
              <div>
                <Label className="text-xs">ABHA Address</Label>
                <Input
                  value={abhaAddress}
                  onChange={(e) => setAbhaAddress(e.target.value)}
                  placeholder="ramesh.patel@abdm"
                  className="mt-1"
                  data-testid="kivi-abha-address"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={saveAbha} disabled={saving || (!abhaNumber && !abhaAddress)} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="kivi-save">
            {saving ? "Saving…" : "Save & Link to Patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
